import type { FidelityTier } from '../utils/fidelity-tiers';
export const FIDELITY_MESSAGE_TYPES = [
  'FIDELITY_1',
  'FIDELITY_2',
  'FIDELITY_3',
] as const;

export type FidelityMessageType = (typeof FIDELITY_MESSAGE_TYPES)[number];

export function isFidelityMessageType(
  value: string,
): value is FidelityMessageType {
  return (FIDELITY_MESSAGE_TYPES as readonly string[]).includes(value);
}

export type FidelityType = 'POINTS';

export type FidelityActionType =
  | 'ADD_POINTS'
  | 'UPDATE_POINTS'
  | 'REMOVE_POINTS'
  | 'REDEEM_REWARD'
  | 'REWARD_CONSUMED'
  | 'ASSIGN_HISTORICAL_REWARD'
  | 'UNLOCK_HISTORICAL_REWARD'
  | 'ADD_BILL_AMOUNT'
  | 'ADD_PREPAID_AMOUNT'
  | 'UPDATE_PREPAID_AMOUNT'
  | 'DISABLE_ACCOUNT'
  | 'CREATE_ACCOUNT'
  | 'USED';

export interface FidelityCustomerSummary {
  customerId?: number;
  customerCode?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  prefix?: string;
  tagsList?: string[];
  imageUrl?: string | null;
  isWalkInCustomer?: boolean;
}

export interface FidelityThreshold {
  lowerBound?: number;
  upperBound?: number;
  points?: number;
  code?: string;
  hexColor?: string;
}

export interface FidelityConf {
  expiresOn?: string | null;
  pointsPolicy?: CreateFidelityConfPayload['pointsPolicy'];
  historicalThresholds?: FidelityThreshold[];
  milestones?: Array<Record<string, unknown>>;
  historicalTiers?: FidelityTier[];
  historicalMilestones?: Array<Record<string, unknown>>;
  fidelityId: number;
  branchCode: string;
  fidelityCode: string;
  fidelityName: string;
  fidelityDescription: string;
  tagsApplied: string;
  tagsAppliedOnDownload: string;
  fidelityType: FidelityType;
  customerCount: number;
  thresholds: FidelityThreshold[];
  createdAt: string | null;
  active: boolean;
  hexColor: string;
  messageType: FidelityMessageType;
}

export interface FidelityOperation {
  customerId?: number;
  branchCode?: string;
  fidelityCode?: string;
  insertedBy?: string;
  actionDate?: string;
  actionType?: FidelityActionType;
  pointsAdded?: number;
  historicalPointsAdded?: number;
  amountAdded?: number;
  rewardName?: string;
  description?: string;
  grantId?: string;
  processedBy?: string;
}

export interface FidelityCard {
  fidelityCardId: number;
  customerId: number;
  fidelityCode: string;
  points: number;
  historicalPoints?: number;
  fidelityCardCode: string;
  activationDate: string | null;
  deactivationDate: string | null;
  prepaidAmount: number;
  used: boolean;
  fidelityOperationList: FidelityOperation[];
  fidelityCardQrUrl: string | null;
  customerDTO: FidelityCustomerSummary | null;
}

export interface FidelityRewardRule {
  code?: string | null;
  tagsAppliedOnRedemption?: string;
  rewardRuleId: string;
  fidelityId: number;
  fidelityCode: string;
  thresholdCode: string | null;
  trigger?: 'POINTS' | 'ENROLLMENT';
  pointsTrack?: 'AVAILABLE' | 'HISTORICAL';
  thresholdPoints?: number;
  rewardType: 'DISCOUNT_CARD' | 'EXPERIENCE_CARD' | 'PRIZE';
  discountCardId?: number | null;
  experienceId?: number | null;
  prizeId?: number | null;
  name?: string;
  description?: string;
  status: 'ACTIVE' | 'ARCHIVED';
}

export interface FidelityRewardGrant {
  grantId: string;
  fidelityCardCode: string;
  fidelityCode: string;
  rewardRuleId: string;
  thresholdCode: string | null;
  rewardType: 'DISCOUNT_CARD' | 'EXPERIENCE_CARD' | 'PRIZE';
  pointsTrack?: 'AVAILABLE' | 'HISTORICAL';
  discountCardId: number | null;
  experienceId: number | null;
  prizeId: number | null;
  rewardSnapshot: Record<string, unknown> | null;
  qrCodeValue?: string;
  pointsSpent?: number;
  pointsDebitedAt?: string | null;
  customerDiscountCard?: FidelityCustomerDiscountCard | null;
  status: 'AVAILABLE' | 'REDEEMED' | 'EXPIRED';
  grantedAt: string;
  expiresOn: string | null;
  redeemedAt: string | null;
  redeemedBy: string | null;
}

/** A discount catalog entry becomes this customer-specific QR instance on award. */
export interface FidelityCustomerDiscountCard {
  usageMode?: 'SINGLE' | 'UNLIMITED' | 'LIMITED';
  maxUses?: number | null;
  usesCount?: number;
  remainingUses?: number | null;
  walletStatus?: 'DOWNLOADABLE' | 'ACTIVE';
  downloadedAt?: string | null;
  permanent?: boolean;
  id: number;
  customerDiscountCardCode: string;
  qrCodeValue: string;
  branchCode: string;
  customerId: number;
  fidelityCardCode: string | null;
  fidelityCode: string | null;
  discountCardId: number;
  grantId: string | null;
  name: string;
  discountValue: number;
  discountType: 'PERCENTAGE';
  status: 'AVAILABLE' | 'USED' | 'EXPIRED';
  assignedAt: string | null;
  expiresOn: string | null;
  usedAt: string | null;
  usedBy: string | null;
}

export interface PageFidelityCard {
  content: FidelityCard[];
  totalPages: number;
  totalElements: number;
  numberOfElements: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

export interface FidelityListParams {
  branchCode: string;
  page?: number;
  size?: number;
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface FidelityCodeParams {
  fidelityCode: string;
}

export interface FidelityConfIdParams {
  fidelityConfId: number;
  branchCode?: string;
}

export interface CreateFidelityConfPayload {
  branchCode: string;
  fidelityName: string;
  fidelityDescription?: string;
  tagsApplied?: string;
  tagsAppliedOnDownload?: string;
  fidelityType: FidelityType;
  pointsPolicy?: {
    mode: 'BANDS' | 'PER_EURO';
    pointsPerEuro?: number;
    rounding?: 'FLOOR';
    eligibleAmount?: 'PAID_TOTAL';
  };
  milestones?: Array<Record<string, unknown>>;
  /** Native membership levels, selected using lifetime points. */
  historicalTiers?: FidelityTier[];
  historicalMilestones?: Array<Record<string, unknown>>;
  expiresOn?: string | null;
  thresholds?: FidelityThreshold[];
  active?: boolean;
  hexColor?: string;
  messageType?: FidelityMessageType;
}

export interface RegisterFidelityCardPayload {
  firstName: string;
  lastName: string;
  prefix: string;
  phone: string;
  email: string;
  privacyConsent: boolean;
  marketingConsent: boolean;
  profilingConsent: boolean;
}

export interface RegisterFidelityCardParams {
  fidelityCode: string;
  customer: RegisterFidelityCardPayload;
}

export interface CheckPublicFidelityCustomerParams {
  fidelityCode: string;
  prefix: string;
  phone: string;
}

export interface RequestPublicFidelityLinkParams {
  fidelityCode: string;
  prefix: string;
  phone: string;
}

export interface CheckPublicFidelityCustomerResult {
  available: boolean;
  customer: FidelityCustomerSummary | null;
}

export interface UpdateFidelityConfPayload extends CreateFidelityConfPayload {
  fidelityId: number;
  fidelityCode: string;
}

export interface DeleteFidelityConfParams {
  fidelityConfId: number;
  branchCode: string;
}

export interface AddThresholdsParams {
  fidelityConfId: number;
  thresholds: FidelityThreshold[];
  branchCode?: string;
}

export interface UpdateThresholdParams {
  fidelityConfId: number;
  threshold: FidelityThreshold;
  branchCode?: string;
}

export interface RemoveThresholdParams {
  fidelityConfId: number;
  code: string;
  branchCode?: string;
}

export interface CreateFidelityCardParams {
  customerId: number;
  fidelityCode: string;
  customerDTO?: FidelityCustomerSummary;
}

export interface FidelityCardsPageParams {
  fidelityCode: string;
  page?: number;
  size?: number;
}

export interface FidelityCardCodeParams {
  fidelityCardCode: string;
}

export interface FidelityCardIdParams {
  fidelityCardId: number;
}

export interface UseFidelityCardParams {
  fidelityCardCode: string;
  processedBy?: string;
}

export interface UpdatePointAmountParams {
  fidelityCardCode: string;
  pointsAmount: number;
  processedBy: string;
}

export interface AddPrepaidAmountParams {
  fidelityCardCode: string;
  amount: number;
}

export interface AddPointsParams {
  fidelityCardCode: string;
  pointsToAdd: number;
}

export interface AddPointsBasedOnAmountParams {
  fidelityCardCode: string;
  amount: number;
  processedBy: string;
}
